import { invalidatePolicyViews } from "../../../lib/policyQueries";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Alert } from "../../../components/ui/Alert";
import { Button } from "../../../components/ui/Button";
import { PageHeader } from "../../../components/ui/PageHeader";
import { ErrorState, LoadingState } from "../../../components/ui/State";
import { Tabs, type TabItem } from "../../../components/ui/Tabs";
import { ApiError } from "../../../lib/api";
import { schedulesApi } from "../../../lib/api/policies";
import { t } from "../../../lib/i18n";
import { useLocale } from "../../../lib/useLocale";

import { ScheduleAssignmentsTab } from "./tabs/ScheduleAssignmentsTab";
import { ScheduleProfileTab } from "./tabs/ScheduleProfileTab";
import { SchedulePreviewTab } from "./tabs/SchedulePreviewTab";
import { computeOverlapErrors } from "./utils/validateWindowOverlap";

type TabKey = "profile" | "assignments" | "preview";
const VALID_TABS: ReadonlyArray<TabKey> = ["profile", "assignments", "preview"];

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

interface ScheduleEditorPageProps {
  /** Optional override for the schedule id; when set, takes precedence over
   *  the ``:id`` route param so the same component can render inside a
   *  side-panel embed driven by ``?id=`` query state. */
  scheduleId?: string;
  /** Called after a successful create — receives the new id. Defaults to a
   *  full-page navigate to ``/policies/schedules/:id``. */
  onCreated?: (id: string) => void;
  /** Called after a delete or when the user cancels — defaults to a
   *  navigate back to ``/policies/schedules``. */
  onClose?: () => void;
}

export function ScheduleEditorPage({ scheduleId, onCreated, onClose }: ScheduleEditorPageProps = {}) {
  useLocale();
  const params = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const id = scheduleId ?? params.id;
  const qc = useQueryClient();
  const isNew = !id || id === "new";

  const tabParam = searchParams.get("tab");
  const activeTab: TabKey = VALID_TABS.includes(tabParam as TabKey)
    ? (tabParam as TabKey)
    : "profile";

  const setActiveTab = (next: TabKey) => {
    const updated = new URLSearchParams(searchParams);
    if (next === "profile") updated.delete("tab");
    else updated.set("tab", next);
    setSearchParams(updated, { replace: false });
  };

  const handleCreated = (newId: string) =>
    onCreated ? onCreated(newId) : navigate(`/policies/schedules/${newId}`);
  const handleClose = () =>
    onClose ? onClose() : navigate("/policies/schedules");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tz, setTz] = useState("Europe/Istanbul");
  const [scheduleDirection, setScheduleDirection] = useState<"active_during" | "inactive_during">("active_during");
  const [isActive, setIsActive] = useState(true);

  const detail = useQuery({
    queryKey: ["schedule", id],
    queryFn: () => schedulesApi.get(id!),
    enabled: !isNew,
  });

  useEffect(() => {
    if (detail.data) {
      setName(detail.data.name);
      setDescription(detail.data.description);
      setTz(detail.data.timezone);
      setScheduleDirection(detail.data.schedule_direction ?? "active_during");
      setIsActive(detail.data.is_active);
    }
  }, [detail.data]);

  const create = useMutation({
    mutationFn: () =>
      schedulesApi.create({
        name,
        description,
        timezone: tz,
        schedule_direction: scheduleDirection,
        is_active: isActive,
      }),
    onSuccess: (data) => {
      void invalidatePolicyViews(qc);
      handleCreated(data.id);
    },
  });

  const patch = useMutation({
    mutationFn: () =>
      schedulesApi.patch(id!, {
        name,
        description,
        timezone: tz,
        schedule_direction: scheduleDirection,
        is_active: isActive,
      }),
    onSuccess: () => {
      void invalidatePolicyViews(qc);
    },
  });

  const overlapErrors = useMemo(
    () => (detail.data ? computeOverlapErrors(detail.data.windows) : []),
    [detail.data],
  );

  const triggerSubmit = () => {
    if (!isNew && overlapErrors.length > 0) {
      setActiveTab("profile");
      return;
    }
    if (isNew) create.mutate();
    else patch.mutate();
  };

  if (!isNew && detail.isLoading) return <LoadingState />;
  if (!isNew && detail.error) {
    return <ErrorState titleKey="states.errorTitle" message={translateError(detail.error)} />;
  }

  const mutation = isNew ? create : patch;
  const errMsg = mutation.error ? translateError(mutation.error) : null;

  const assignmentsCount = detail.data?.assignments.length;
  const tabs: TabItem<TabKey>[] = [
    { value: "profile", label: t("schedules.tabs.profile") },
    {
      value: "assignments",
      label: t("schedules.tabs.assignments"),
      count: assignmentsCount,
    },
    { value: "preview", label: t("schedules.tabs.preview") },
  ];

  return (
    <div>
      <PageHeader
        title={isNew ? t("schedules.editor.titleNew") : t("schedules.editor.titleEdit")}
        subtitle={detail.data?.name}
        actions={
          <Button variant="secondary" onClick={handleClose}>
            {t("schedules.editor.back")}
          </Button>
        }
      />

      {(create.isSuccess || patch.isSuccess) && <Alert tone="info">{t("operations.schedules.saved")}</Alert>}
      {!isNew && detail.data && (
        <div style={{ marginBottom: 16 }}>
          <Tabs<TabKey>
            tabs={tabs}
            value={activeTab}
            onChange={setActiveTab}
            ariaLabel={t("schedules.editor.titleEdit")}
          />
        </div>
      )}

      {(isNew || activeTab === "profile") && (
        <ScheduleProfileTab
          scheduleId={id}
          isNew={isNew}
          detail={detail.data ?? null}
          name={name}
          setName={setName}
          description={description}
          setDescription={setDescription}
          tz={tz}
          setTz={setTz}
          scheduleDirection={scheduleDirection}
          setScheduleDirection={setScheduleDirection}
          isActive={isActive}
          setIsActive={setIsActive}
          onSubmit={triggerSubmit}
          submitting={mutation.isPending}
          errMsg={errMsg}
          overlapErrors={overlapErrors}
        />
      )}

      {!isNew && activeTab === "assignments" && detail.data && (
        <ScheduleAssignmentsTab
          scheduleId={detail.data.id}
          assignments={detail.data.assignments}
        />
      )}

      {!isNew && activeTab === "preview" && detail.data && (
        <SchedulePreviewTab
          timezone={detail.data.timezone}
          windows={detail.data.windows}
        />
      )}
    </div>
  );
}
